//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown } from 'lucide-react';
import Person from '../../../icons/AiPerson.svg';
import UserResponse from './UserResponse';
import AIResponse from './AIResponse';
import ToolCallBox from './ToolCallBox';
import IterationWarningBanner from './IterationWarningBanner';
import PlanApprovalCard from './PlanApprovalCard';
import ExecutionReceiptCard from './ExecutionReceiptCard';
import ExecutionTerminalNotice from './ExecutionTerminalNotice';
import IngestionBlockedBanner from './IngestionBlockedBanner';
import TurnTraceControl from './TurnTraceControl';
import TurnTracePanel from './TurnTracePanel';
import { useToggleSet } from './useToggleSet';
import {
  receiptAnnouncement,
  terminalAnnouncement,
} from './executionAnnouncement';
import { groupMessagesByTurn } from './turnGrouping';
import {
  MessageScrollerProvider,
  MessageScroller,
  MessageScrollerViewport,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerButton,
  useMessageScrollerScrollable,
} from '../../ui/message-scroller';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';
import type {
  HitlDecision,
  IterationWarningEvent,
  PlanApprovalRequest,
} from '../../../types/streaming';
import type { ChatMessage } from './AIModule';
import { renderTaskIcon, type TaskIcon } from './taskIcon';

interface TaskStartersInfo {
  taskId: string;
  taskName: string;
  conversation_starters: { label: string; prompt: string }[];
}

// Renders the existing "jump to latest" button, augmented with an unread
// dot when new messages have streamed in while the reader is scrolled away
// from the live edge. Must render as a descendant of MessageScrollerProvider
// since useMessageScrollerScrollable reads from its context.
const JumpToLatestButton: React.FC<{ messageCount: number }> = ({
  messageCount,
}) => {
  const { t } = useTranslation();
  const scrollable = useMessageScrollerScrollable();
  const [hasNewMessages, setHasNewMessages] = useState(false);

  // Derived-during-render state (React's documented alternative to an
  // effect + setState for "respond to a prop/value changing since the last
  // render") — avoids the extra render+effect pass, and the
  // react-hooks/set-state-in-effect lint rule flags the effect-based form
  // as a cascading-render risk.
  //
  // If messageCount and scrollable.end both change in the same render, both
  // branches below call setHasNewMessages — React batches these updates and
  // applies them in the order they were called, so scrollable.end becoming
  // true (arriving-at-the-edge) always wins over a same-render new-message
  // arrival. That's the desired outcome: if the reader is at the live edge
  // by the time this render is committed, there's nothing "new" left to flag.
  const [prevMessageCount, setPrevMessageCount] = useState(messageCount);
  const [wasAtEnd, setWasAtEnd] = useState(scrollable.end);

  if (messageCount !== prevMessageCount) {
    setPrevMessageCount(messageCount);
    if (messageCount > prevMessageCount && !scrollable.end) {
      setHasNewMessages(true);
    }
  }
  if (scrollable.end !== wasAtEnd) {
    setWasAtEnd(scrollable.end);
    // Returning to the live edge (by clicking the button, or scrolling down
    // manually) clears the indicator.
    if (scrollable.end) setHasNewMessages(false);
  }

  return (
    <MessageScrollerButton onClick={() => setHasNewMessages(false)}>
      <span className="relative inline-flex">
        <ArrowDown />
        {hasNewMessages && (
          <span
            aria-hidden="true"
            data-testid="ai-module-new-messages-indicator"
            className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-red-500"
          />
        )}
      </span>
      <span className="sr-only">
        {hasNewMessages
          ? t('new-messages-scroll-to-end', 'New messages — scroll to end')
          : t('scroll-to-end', 'Scroll to end')}
      </span>
    </MessageScrollerButton>
  );
};

export interface ChatThreadProps {
  messages: ChatMessage[];
  isAIBuffering: boolean;
  isIngestionActive: boolean;
  /** True from when a HITL decision is sent until the backend pauses again
   * or the turn ends. A single approval can now silently resolve many other
   * pending rows in one background cascade — disable the rest so a second
   * click can't race the backend's one-turn-per-conversation guard. */
  isHitlTurnInFlight: boolean;
  iterationWarning: IterationWarningEvent | null;
  /** True while at least one tool_approval_request is pending (several can
   * be pending at once when the model dispatches parallel mutating calls) —
   * used only for the a11y live-status announcement below. */
  hasHitlApproval: boolean;
  /** Execution events (keys) a screen reader has already been told about. Owned by
   * the parent because this component remounts when the Chats tab is left and
   * re-entered, which would otherwise read the last receipt out again. */
  announcedExecutionKeys?: string[];
  /** Called once with the key of each execution event this component announces. */
  onExecutionAnnounced?: (key: string) => void;
  onHitlDecide: (decision: HitlDecision) => void;
  planApproval: PlanApprovalRequest | null;
  onPlanApprove: () => void;
  onPlanReject: () => void;
  onPlanRequestChanges: (feedback: string) => void;
  onDismissWarning: () => void;
  loopId: string;
  selectedLoopId: string | null;
  showWelcome: boolean;
  activeTaskInfo: { name: string; icon?: TaskIcon } | null;
  availableLoops: { id: string; name: string }[];
  onLoopSelect: (id: string) => void;
  selectedTaskStarters: TaskStartersInfo | null;
  darkMode: boolean;
  userName: string;
  onTaskStarterClick: (taskId: string, prompt: string) => void;
}

const ChatThread: React.FC<ChatThreadProps> = ({
  messages,
  isAIBuffering,
  isIngestionActive,
  isHitlTurnInFlight,
  iterationWarning,
  hasHitlApproval,
  announcedExecutionKeys,
  onExecutionAnnounced,
  onHitlDecide,
  planApproval,
  onPlanApprove,
  onPlanReject,
  onPlanRequestChanges,
  onDismissWarning,
  loopId,
  selectedLoopId,
  showWelcome,
  activeTaskInfo,
  availableLoops,
  onLoopSelect,
  selectedTaskStarters,
  darkMode,
  userName,
  onTaskStarterClick,
}) => {
  const { t } = useTranslation();

  const lastUserMessageIndex = messages.reduce(
    (acc, chat, index) => (chat.sender === 'user' ? index : acc),
    -1,
  );

  // The turn-scoped tool trace (P2, additive): grouped once per message-array
  // identity change, not on every render. The legacy (turnId: null) bucket
  // never gets a control — there is no stable id to key its DOM/state on,
  // and in practice it never carries tool rows anyway (history restore drops
  // tool calls entirely before they reach this array).
  const turnGroups = useMemo(() => groupMessagesByTurn(messages), [messages]);
  const turnArtifactByLastMessageIndex = useMemo(() => {
    const byLastIndex = new Map<number, (typeof turnGroups)[number]>();
    for (const group of turnGroups) {
      // A turn with no non-internal tool calls has nothing for the artifact
      // to show — TurnTraceControl already renders null for it, but leaving
      // the group in this map still mounts the wrapper div/MessageScrollerItem
      // around nothing, for every plain Q&A turn. Excluded at the source
      // instead of downstream, so withTurnArtifact never runs for it at all.
      if (
        !group.turnId ||
        group.messages.length === 0 ||
        group.visibleToolCount === 0
      ) {
        continue;
      }
      // group.lastMessageIndex was recorded during groupMessagesByTurn's own
      // single pass over `messages` — no separate indexOf() rescan needed
      // here (found in review of #988: performance; this used to be a
      // second full-array scan per turn, riding on the same every-streamed-
      // token trigger as the grouping pass itself).
      byLastIndex.set(group.lastMessageIndex, group);
    }
    return byLastIndex;
  }, [turnGroups]);

  const [openTurnIds, toggleTurnTrace] = useToggleSet();

  // Selecting text is intent: don't yank the transcript out from under a
  // reader who is mid-selection just because a new chunk streamed in.
  // Scoped to selections that start inside the transcript itself, so
  // selecting text elsewhere on the page (e.g. the sidebar) doesn't pause
  // auto-follow here.
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isSelectingText, setIsSelectingText] = useState(false);

  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = document.getSelection();
      const hasSelection = !!selection && selection.toString().length > 0;
      const anchorNode = selection?.anchorNode ?? null;
      const withinTranscript = !!(
        anchorNode &&
        contentRef.current &&
        contentRef.current.contains(anchorNode)
      );
      setIsSelectingText(hasSelection && withinTranscript);
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () =>
      document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  // The latest execution receipt / end-of-run notice in the transcript, as one
  // short sentence for screen readers. Those messages are rendered into the
  // transcript, which is not itself a live region, so without this a screen-reader
  // user is never told that an execution finished or needs something from them.
  // Only one that arrived over the socket during this session (`live`) is spoken,
  // and each only once: a receipt restored with the conversation's history (which
  // loads after this component mounts), seen again after switching loops, or after
  // leaving and returning to the Chats tab (the parent keeps the announced keys, as
  // this component remounts) is not read out again (review of #1054).
  const loopKey = selectedLoopId || loopId || '';
  let latestExecutionEvent: { key: string; text: string } | null = null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m.sender === 'execution_receipt') {
      latestExecutionEvent = m.live
        ? {
            key: `${loopKey}|receipt-${m.planCallId}`,
            text: receiptAnnouncement(m.receipt, m.terminalStatus),
          }
        : null;
      break;
    }
    if (m.sender === 'execution_terminal') {
      latestExecutionEvent = m.live
        ? {
            key: `${loopKey}|terminal-${m.event.plan_call_id}-${m.event.status}`,
            text: terminalAnnouncement(m.event),
          }
        : null;
      break;
    }
  }
  // State is adjusted while rendering (React's documented pattern for reacting to
  // a changed value, no effect needed).
  const [executionAnnouncement, setExecutionAnnouncement] = useState<{
    announced: string[];
    text: string;
    buffering: boolean;
  }>({ announced: [], text: '', buffering: isAIBuffering });
  let nextAnnouncement = executionAnnouncement;
  if (isAIBuffering !== executionAnnouncement.buffering) {
    // A NEW turn starting (buffering turning on) clears the previous run's
    // sentence. Not every render while buffering: the server sends the receipt
    // before the assistant's closing text, so a receipt normally arrives while
    // the turn is still buffering, and its sentence must survive until that ends.
    nextAnnouncement = {
      ...nextAnnouncement,
      buffering: isAIBuffering,
      text: isAIBuffering ? '' : nextAnnouncement.text,
    };
  }
  if (
    latestExecutionEvent &&
    !nextAnnouncement.announced.includes(latestExecutionEvent.key) &&
    !(announcedExecutionKeys ?? []).includes(latestExecutionEvent.key)
  ) {
    nextAnnouncement = {
      ...nextAnnouncement,
      announced: [...nextAnnouncement.announced, latestExecutionEvent.key],
      text: latestExecutionEvent.text,
    };
  }
  if (nextAnnouncement !== executionAnnouncement) {
    setExecutionAnnouncement(nextAnnouncement);
  }
  // Tell the parent which event was just announced, so it outlives this component.
  const lastAnnouncedKey =
    executionAnnouncement.announced[executionAnnouncement.announced.length - 1];
  useEffect(() => {
    if (lastAnnouncedKey) onExecutionAnnounced?.(lastAnnouncedKey);
  }, [lastAnnouncedKey, onExecutionAnnounced]);

  // Low-noise status text for assistive tech: announces the meaningful
  // state transitions (a turn starting/finishing, an approval being
  // needed, an execution finishing) rather than every streamed token.
  const liveStatus = planApproval
    ? t('live-status-plan-approval', 'Plan approval needed')
    : hasHitlApproval
      ? t('live-status-hitl-approval', 'Tool approval needed')
      : isIngestionActive
        ? t(
            'live-status-ingestion',
            'MCP tool ingestion in progress, Loop Genie is temporarily unavailable',
          )
        : isAIBuffering
          ? t('live-status-responding', 'Loop Genie is responding')
          : executionAnnouncement.text;

  return (
    <div
      ref={contentRef}
      className={cn(
        'flex min-h-0 flex-1 flex-col',
        darkMode ? 'bg-[#1e1e1e] text-[#e0e0e0]' : undefined,
      )}
    >
      {/* Announces meaningful state changes only (not per streamed token) so
          screen reader users aren't buried in noise. */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {liveStatus}
      </div>
      {/* Scrollable message area */}
      <MessageScrollerProvider
        autoScroll={!isSelectingText}
        defaultScrollPosition="last-anchor"
        // Keeps ~one line of the previous turn in view above the new one,
        // and leaves breathing room at the scroll edges — tunable if the
        // design wants a different amount of context/margin.
        scrollPreviousItemPeek={48}
        scrollMargin={16}
      >
        <MessageScroller className="flex-1">
          <MessageScrollerViewport
            data-testid="id-ai-module-content"
            id="id-ai-module-content"
            className="p-4"
          >
            <MessageScrollerContent>
              {showWelcome ? (
                <MessageScrollerItem>
                  <div
                    data-testid="id-ai-chat-history-container"
                    id="id-ai-chat-history-container"
                  >
                    <div
                      id="id-ai-module-welcome-image-container"
                      data-testid="ai-module-welcome-image-container"
                      className="flex items-center justify-center"
                    >
                      <img
                        src={Person}
                        alt={t('ai-person')}
                        style={{ width: 150, height: 150, marginTop: 20 }}
                      />
                    </div>
                    {availableLoops.length > 0 ? (
                      <>
                        <p className="flex items-center justify-center font-[Roboto] text-[18px] font-semibold leading-6 text-[#1C262E]">
                          {t('hey-user', { name: userName })}
                        </p>
                        <AIResponse
                          loopId={selectedLoopId || loopId}
                          message={t('ai-prompt-message')}
                          subMessage={t('ai-prompt-sub-message')}
                          loops={availableLoops}
                          onLoopSelect={onLoopSelect}
                          isAIBuffering={isAIBuffering}
                        />
                      </>
                    ) : (
                      // Deliberately no "let's get started with the
                      // conversation" greeting here — there is nothing to
                      // start yet. Reuses the same copy already shown
                      // elsewhere in the app for this exact constraint
                      // (SearchBox's loop-required tooltip) instead of
                      // inventing new empty-state wording.
                      <AIResponse
                        loopId={selectedLoopId || loopId}
                        message={t('tooltip-no-loops')}
                        isAIBuffering={isAIBuffering}
                      />
                    )}
                  </div>
                </MessageScrollerItem>
              ) : (
                <>
                  {/* Task header or welcome card */}
                  {activeTaskInfo ? (
                    <MessageScrollerItem>
                      <div
                        id="id-ai-task-header"
                        data-testid="id-ai-task-header"
                        className="flex flex-col items-center gap-2 px-4 pb-3 pt-6"
                      >
                        <div
                          id="id-ai-module-task-icon-container"
                          data-testid="ai-module-task-icon-container"
                          className="flex items-center justify-center overflow-hidden rounded-full text-2xl"
                          style={{
                            width: 48,
                            height: 48,
                            background: activeTaskInfo.icon?.color || '#E8F0FE',
                          }}
                        >
                          {renderTaskIcon(
                            activeTaskInfo.icon,
                            activeTaskInfo.name,
                            { testId: 'ai-module-task-emoji' },
                          )}
                        </div>
                        <span className="text-center text-[16px] font-semibold leading-snug text-[#1a1a1a]">
                          {activeTaskInfo.name}
                        </span>
                      </div>
                    </MessageScrollerItem>
                  ) : (
                    <MessageScrollerItem>
                      <div
                        data-testid="id-ai-prompts-container"
                        id="id-ai-prompts-container"
                      >
                        <AIResponse
                          loopId={selectedLoopId || loopId}
                          message={t('ai-welcome-msg', { name: userName })}
                          isAIBuffering={false}
                          showFeedback={false}
                        />
                      </div>
                    </MessageScrollerItem>
                  )}

                  {/* Message list */}
                  <div
                    data-testid="id-chat-history-container"
                    id="id-chat-history-container"
                  >
                    {messages.map((chat, index) => {
                      const isLast = index === messages.length - 1;

                      // Cut-over (P3): non-gated tool calls no longer occupy
                      // the column — their record lives in the turn artifact
                      // below. A gated call keeps its inline card only while
                      // still awaiting a decision (toolStatus ===
                      // 'pending_approval'); once resolved, by any means, it
                      // moves to the turn artifact like any other call (S3c
                      // — see the toolStatus check in the tool branch below
                      // for why this isn't keyed on gateDecision instead).
                      const turnGroupHere =
                        turnArtifactByLastMessageIndex.get(index);
                      const withTurnArtifact = (
                        element: React.ReactNode,
                        // Only the tool branch below passes this — the
                        // scroll anchor that a hidden tool row would have
                        // carried moves onto the artifact that replaces it
                        // (FR-018). Every other message type keeps its own
                        // anchor untouched.
                        artifactScrollAnchor = false,
                      ): React.ReactNode => {
                        if (!turnGroupHere) {
                          // A turn whose only tool call is internal (e.g. a
                          // lone search_tools) has no artifact here to carry
                          // this row's scroll anchor onto, and `element` is
                          // `null` for a hidden tool row — without this,
                          // being the newest content in the conversation
                          // dropped the anchor entirely rather than moving
                          // it anywhere (found in review of #988). Reattach
                          // it onto an otherwise-empty scroller item instead
                          // of letting it vanish.
                          if (artifactScrollAnchor && !element) {
                            return (
                              <MessageScrollerItem
                                key={`no-artifact-anchor-${index}`}
                                scrollAnchor
                              />
                            );
                          }
                          return element;
                        }
                        const turnId = turnGroupHere.turnId as string;
                        // One line per turn regardless of how many
                        // approvals it took — gating is not bounded, and
                        // the default Approve button grants scope: 'once',
                        // so a plan's mutations gate individually (S3b;
                        // FR-014b). Pending approvals are deliberately
                        // excluded: they still have their own full card.
                        const { approvedCount, rejectedCount } = turnGroupHere;
                        const grantedText =
                          approvedCount > 0
                            ? t(
                                'ai-turn-approvals-granted',
                                `${approvedCount} approval${approvedCount === 1 ? '' : 's'} granted`,
                                { count: approvedCount },
                              )
                            : '';
                        const rejectedText =
                          rejectedCount > 0
                            ? t(
                                'ai-turn-approvals-rejected',
                                `${rejectedCount} rejected`,
                                { count: rejectedCount },
                              )
                            : '';
                        const approvalSummaryText = [grantedText, rejectedText]
                          .filter(Boolean)
                          .join(', ');
                        return (
                          <React.Fragment key={`row-${turnId}`}>
                            {element}
                            <MessageScrollerItem
                              key={`turn-trace-${turnId}`}
                              scrollAnchor={artifactScrollAnchor}
                            >
                              <div
                                data-testid={`ai-module-turn-trace-${turnId}`}
                                className="mb-[10px] ml-[42px]"
                              >
                                {approvalSummaryText && (
                                  <div
                                    data-testid={`ai-module-turn-approval-summary-${turnId}`}
                                    className="mb-1 text-xs text-slate-500"
                                  >
                                    {approvalSummaryText}
                                  </div>
                                )}
                                <TurnTraceControl
                                  turnId={turnId}
                                  // displayToolCount/displayCompletedCount
                                  // already exclude rejected calls — a
                                  // rejected call never ran, so it must not
                                  // count as "completed" alongside one that
                                  // did (found in review of #988). Computed
                                  // once in turnGrouping.ts now, not here —
                                  // this file doing its own subtraction is
                                  // what let it disagree with the control's
                                  // render gate (found in review of #988,
                                  // second round). No separate
                                  // hasVisibleContent is passed any more —
                                  // this map's own visibleToolCount === 0
                                  // exclusion just above (this file, ~line
                                  // 188) is now the only place that decision
                                  // is made; a turn ever reaches this call
                                  // site at all only when it belongs here,
                                  // including an all-rejected turn (found in
                                  // review of #988, fourth round —
                                  // Constitution Principle VII). TurnTracePanel's
                                  // own row list still uses visibleToolCount/
                                  // toolRows, unaffected — it must still show
                                  // the rejected row itself.
                                  toolCount={turnGroupHere.displayToolCount}
                                  completedCount={
                                    turnGroupHere.displayCompletedCount
                                  }
                                  isRunning={turnGroupHere.isRunning}
                                  hasActiveExecution={
                                    turnGroupHere.hasActiveExecution
                                  }
                                  isOpen={openTurnIds.has(turnId)}
                                  onToggle={() => toggleTurnTrace(turnId)}
                                />
                                <TurnTracePanel
                                  turnId={turnId}
                                  rows={turnGroupHere.toolRows}
                                  isOpen={openTurnIds.has(turnId)}
                                />
                              </div>
                            </MessageScrollerItem>
                          </React.Fragment>
                        );
                      };

                      if (chat.sender === 'user') {
                        return withTurnArtifact(
                          <MessageScrollerItem
                            key={index}
                            // Anchor to the user's most recent message (not
                            // the AI's reply) so it stays visible near the
                            // top while the response streams in below it,
                            // instead of the reply pushing it off-screen.
                            scrollAnchor={index === lastUserMessageIndex}
                          >
                            <div
                              id={`id-ai-module-user-chat-${index}`}
                              data-testid={`ai-module-user-chat-${index}`}
                              className="mb-[10px] flex justify-end"
                            >
                              <UserResponse message={chat.message} />
                            </div>
                          </MessageScrollerItem>,
                        );
                      }
                      if (chat.sender === 'tool') {
                        if (
                          chat.turnId &&
                          chat.toolStatus !== 'pending_approval'
                        ) {
                          // Hide only when a turn artifact exists to carry
                          // this call's record — a tool message with no
                          // turn stamp (anything from before this feature)
                          // has nowhere else for its record to live, so it
                          // keeps rendering exactly as it always has
                          // (FR-002). Hidden here: anything not still
                          // waiting on the user — never gated, gated and
                          // approved, gated and rejected, or resolved by a
                          // decision this tab never made (a wider grant
                          // scope, or the plan approval covering an
                          // already-gated mutation). Keyed on toolStatus,
                          // not gateDecision (S3b's original check): the
                          // latter is only ever set by a click on this
                          // exact card in this exact tab, so a call
                          // resolved any other way carried gateDecision:
                          // undefined forever and stayed pinned inline with
                          // a spinner — the same "wall of cards" problem
                          // S3b existed to fix, for a case S3b's own check
                          // couldn't see (found live, S3c). toolStatus is
                          // ground truth for "still needs a decision"
                          // regardless of who or what made it. Only a still-
                          // pending approval keeps its card. Either way the
                          // turn artifact inherits this row's scroll anchor
                          // so a running turn still tracks (FR-018).
                          return withTurnArtifact(
                            null,
                            isLast && !isAIBuffering,
                          );
                        }
                        return withTurnArtifact(
                          <MessageScrollerItem
                            key={chat.toolCallId}
                            scrollAnchor={isLast && !isAIBuffering}
                          >
                            <div
                              id={`id-ai-module-tool-chat-${index}`}
                              data-testid={`ai-module-tool-chat-${index}`}
                              className="mb-[10px] ml-[42px]"
                            >
                              <ToolCallBox
                                toolName={chat.toolName}
                                toolArgs={chat.toolArgs}
                                toolResult={chat.toolResult}
                                resultBlocks={chat.toolResultBlocks}
                                outputRef={chat.toolOutputRef}
                                toolCallId={chat.toolCallId}
                                toolStatus={chat.toolStatus}
                                approvalDisabled={
                                  isIngestionActive || isHitlTurnInFlight
                                }
                                taskActive={activeTaskInfo !== null}
                                onApprove={(scope) =>
                                  onHitlDecide({
                                    action: 'approve',
                                    scope,
                                    tool_call_id: chat.toolCallId,
                                  })
                                }
                                onReject={() =>
                                  onHitlDecide({
                                    action: 'reject',
                                    tool_call_id: chat.toolCallId,
                                  })
                                }
                              />
                            </div>
                          </MessageScrollerItem>,
                        );
                      }
                      if (chat.sender === 'plan_approval') {
                        return withTurnArtifact(
                          <MessageScrollerItem
                            key={chat.planCallId}
                            scrollAnchor={isLast && !isAIBuffering}
                          >
                            <div
                              id={`id-ai-module-plan-chat-${index}`}
                              data-testid={`ai-module-plan-chat-${index}`}
                              className="mb-[10px] ml-[42px]"
                            >
                              <PlanApprovalCard
                                plan={chat.plan}
                                decision={chat.decision}
                                onApprove={onPlanApprove}
                                onReject={onPlanReject}
                                onRequestChanges={onPlanRequestChanges}
                              />
                            </div>
                          </MessageScrollerItem>,
                        );
                      }
                      if (chat.sender === 'execution_receipt') {
                        return withTurnArtifact(
                          <MessageScrollerItem
                            key={`receipt-${chat.planCallId}`}
                            scrollAnchor={isLast && !isAIBuffering}
                          >
                            <div
                              id={`id-ai-module-receipt-chat-${index}`}
                              data-testid={`ai-module-receipt-chat-${index}`}
                              className="mb-[10px] ml-[42px]"
                            >
                              <ExecutionReceiptCard
                                receipt={chat.receipt}
                                terminalStatus={chat.terminalStatus}
                              />
                            </div>
                          </MessageScrollerItem>,
                        );
                      }
                      if (chat.sender === 'execution_terminal') {
                        return withTurnArtifact(
                          <MessageScrollerItem
                            key={`terminal-${index}`}
                            scrollAnchor={isLast && !isAIBuffering}
                          >
                            <div
                              id={`id-ai-module-terminal-chat-${index}`}
                              data-testid={`ai-module-terminal-chat-${index}`}
                              className="mb-[10px] ml-[42px]"
                            >
                              <ExecutionTerminalNotice event={chat.event} />
                            </div>
                          </MessageScrollerItem>,
                        );
                      }
                      return withTurnArtifact(
                        <MessageScrollerItem
                          key={index}
                          scrollAnchor={isLast && !isAIBuffering}
                        >
                          <div
                            id={`id-ai-module-ai-chat-${index}`}
                            data-testid={`ai-module-ai-chat-${index}`}
                            className="mb-[36px]"
                          >
                            <AIResponse
                              loopId={selectedLoopId || loopId}
                              message={chat.message}
                              subMessage={chat.subMessage}
                              isBufferMessage={
                                chat.message === t('ai-buffer-msg') ||
                                chat.message === t('searching-buffer-msg') ||
                                chat.message.startsWith(t('processing-ai')) ||
                                chat.message.startsWith(t('plan-complete')) ||
                                chat.message.startsWith(t('using-tool')) ||
                                chat.message.startsWith(t('validating'))
                              }
                              isAIBuffering={isLast && isAIBuffering}
                            />
                          </div>
                        </MessageScrollerItem>,
                      );
                    })}
                  </div>

                  {/* Wave-dots buffering indicator */}
                  {isAIBuffering && (
                    <MessageScrollerItem>
                      <>
                        <style>{`
                        @keyframes waveDot {
                          0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
                          30% { transform: translateY(-6px); opacity: 1; }
                        }
                      `}</style>
                        <div
                          id="id-ai-response-wave-dots"
                          data-testid="ai-response-wave-dots"
                          className="mt-1.5 flex items-center gap-[5px] pl-0.5"
                          style={{ marginLeft: 42 }}
                        >
                          {[0, 1, 2].map((i) => (
                            <span
                              key={i}
                              style={{
                                width: 7,
                                height: 7,
                                borderRadius: '50%',
                                backgroundColor: '#6B7FD7',
                                display: 'inline-block',
                                animation: 'waveDot 1.2s ease-in-out infinite',
                                animationDelay: `${i * 0.22}s`,
                              }}
                            />
                          ))}
                        </div>
                      </>
                    </MessageScrollerItem>
                  )}

                  {/* Task quick starters */}
                  {selectedTaskStarters &&
                    selectedTaskStarters.conversation_starters.length > 0 && (
                      <MessageScrollerItem>
                        <div
                          id="id-ai-module-starters-padding"
                          data-testid="ai-module-starters-padding"
                          className="px-4 py-2"
                        >
                          <div
                            id="id-ai-module-starters-ai-container"
                            data-testid="ai-module-starters-ai-container"
                            className="mb-[36px]"
                          >
                            <AIResponse
                              loopId={selectedLoopId || loopId}
                              message={selectedTaskStarters.taskName}
                              subMessage={
                                <div
                                  id="id-ai-module-quick-starters"
                                  data-testid="ai-module-quick-starters"
                                  aria-label={t('quick-starters')}
                                >
                                  <div
                                    id="id-ai-module-starters-info"
                                    data-testid="ai-module-starters-info"
                                    className="text-[14px] leading-6 text-[#1C262E]"
                                  >
                                    {t('quick-starters-intro', {
                                      taskName: selectedTaskStarters.taskName,
                                    })}
                                  </div>
                                  <div
                                    id="id-ai-module-starters-divider"
                                    data-testid="ai-module-starters-divider"
                                    className="my-2 border-t border-[#D3D9DE]"
                                  />
                                  <div
                                    id="id-ai-module-starters-label-wrapper"
                                    data-testid="ai-module-starters-label-wrapper"
                                  >
                                    <span
                                      id="id-ai-module-starters-section-label"
                                      data-testid="ai-module-starters-section-label"
                                      className="text-[16px] font-semibold leading-6 text-[#1C262E]"
                                    >
                                      {t('task-starters-section-label', {
                                        taskName: selectedTaskStarters.taskName,
                                      })}
                                      :
                                    </span>
                                  </div>
                                  <div
                                    id="id-ai-module-starters-container"
                                    data-testid="ai-module-starters-container"
                                    className="mt-2 flex flex-wrap gap-2"
                                  >
                                    {selectedTaskStarters.conversation_starters.map(
                                      (s, idx) => (
                                        <Button
                                          key={`${selectedTaskStarters.taskId}-${idx}`}
                                          variant="outline"
                                          size="sm"
                                          title={s.prompt}
                                          disabled={
                                            isAIBuffering || isIngestionActive
                                          }
                                          onClick={() => {
                                            if (
                                              isAIBuffering ||
                                              isIngestionActive
                                            )
                                              return;
                                            onTaskStarterClick(
                                              selectedTaskStarters.taskId,
                                              s.prompt,
                                            );
                                          }}
                                          aria-label={`${t('starter')}: ${s.label}`}
                                          className="rounded-[6px] border-[#E1E5E9] bg-[#F5F5FA] font-normal text-[#1C262E] shadow-[0px_4px_4px_0px_#00000012] hover:bg-gray-100"
                                        >
                                          {s.label}
                                        </Button>
                                      ),
                                    )}
                                  </div>
                                </div>
                              }
                              isBufferMessage={false}
                              isAIBuffering={isAIBuffering}
                            />
                          </div>
                        </div>
                      </MessageScrollerItem>
                    )}
                </>
              )}
            </MessageScrollerContent>
          </MessageScrollerViewport>
          <JumpToLatestButton messageCount={messages.length} />
        </MessageScroller>
      </MessageScrollerProvider>

      {/* Sticky panels below the scroll area */}
      {iterationWarning && (
        <IterationWarningBanner
          warning={iterationWarning}
          onDismiss={onDismissWarning}
        />
      )}
      <IngestionBlockedBanner isActive={isIngestionActive} />
      {/* Both the pending plan card and the pending tool-approval box render
          inline in the message list above (like any other tool call box),
          so they scroll with the turn and remain collapsible historical
          records once decided — no floating panel here. */}
    </div>
  );
};

export default ChatThread;
